import { useMemo } from "react";
import {
  type CfarOffer,
  type DelayOffer,
  DisruptionOfferType,
  type MissedConnectionOffer,
  CfarOfferType,
} from "redmond";
import {
  getExperimentVariant,
  useExperiments,
  HTS_FDA_MARKETING,
  AVAILABLE,
} from "../../../../../context/experiments";

interface UseHtsFlexibilityParams {
  disruptionOffer: (DelayOffer | MissedConnectionOffer)[] | undefined;
  cfarOffer: CfarOffer[] | undefined;
  hasActiveRefundableFare: boolean;
}

type UseHtsFlexibilityResult =
  | {
      isHtsCfar: false;
      htsCfarOffer: undefined;
      isHtsDisruption: false;
      htsDisruptionOffer: undefined;
    }
  | {
      isHtsCfar: true;
      htsCfarOffer: CfarOffer;
      isHtsDisruption: false;
      htsDisruptionOffer: undefined;
    }
  | {
      isHtsCfar: false;
      htsCfarOffer: undefined;
      isHtsDisruption: true;
      htsDisruptionOffer: DelayOffer | MissedConnectionOffer;
    }
  | {
      isHtsCfar: true;
      htsCfarOffer: CfarOffer;
      isHtsDisruption: true;
      htsDisruptionOffer: DelayOffer | MissedConnectionOffer;
    };

export const useHtsAirFlexibility = ({
  disruptionOffer,
  cfarOffer,
  hasActiveRefundableFare,
}: UseHtsFlexibilityParams): UseHtsFlexibilityResult => {
  const expState = useExperiments();

  const isDisruptionMerchandisingEnabled = useMemo(
    () =>
      getExperimentVariant(expState.experiments, HTS_FDA_MARKETING) ===
      AVAILABLE,
    [expState]
  );

  const isHtsDisruption = useMemo(
    () =>
      disruptionOffer != null &&
      disruptionOffer.length > 0 &&
      disruptionOffer[0].offerType?.OfferType === DisruptionOfferType.Hts &&
      isDisruptionMerchandisingEnabled,
    [disruptionOffer, isDisruptionMerchandisingEnabled]
  );

  const isHtsCfar = useMemo(
    () =>
      // If we have a refundable fare selected, we need to treat it as if we don't have a standalone HTS CFAR
      !hasActiveRefundableFare &&
      cfarOffer != null &&
      cfarOffer.length > 0 &&
      cfarOffer[0].offerType?.OfferType === CfarOfferType.Hts,
    [cfarOffer]
  );

  if (isHtsCfar && isHtsDisruption) {
    return {
      isHtsCfar: true,
      htsCfarOffer: cfarOffer![0],
      isHtsDisruption: true,
      htsDisruptionOffer: disruptionOffer![0],
    };
  }

  if (isHtsCfar) {
    return {
      isHtsCfar: true,
      htsCfarOffer: cfarOffer![0],
      isHtsDisruption: false,
      htsDisruptionOffer: undefined,
    };
  }

  if (isHtsDisruption) {
    return {
      isHtsCfar: false,
      htsCfarOffer: undefined,
      isHtsDisruption: true,
      htsDisruptionOffer: disruptionOffer![0],
    };
  }

  return {
    isHtsCfar: false,
    htsCfarOffer: undefined,
    isHtsDisruption: false,
    htsDisruptionOffer: undefined,
  };
};
